import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as Yup from "yup";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {QueryJoin} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QueryJoin";
import {QTableVariant} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableVariant";
import DynamicFormUtils, {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {
   runOnLoadFormAdjuster,
   runOnChangeFormAdjuster,
   evaluateFieldRules,
   FormAdjusterCallbacks,
} from "qqq/components/forms/formAdjustersAndRules";
import {FieldRule} from "qqq/models/fields/FieldRules";
import {RowBannerState} from "./RowEditContext";
import {claimActiveEditor, releaseActiveEditor} from "./globalRowEditRegistry";
import Client from "qqq/utils/qqq/Client";
import {SAVE_SUCCESS_BANNER_MS} from "./rowEditConstants";

const qController = Client.getInstance();


/***************************************************************************
 * Run `fn` once `setFieldValueRef.current` becomes non-null (i.e. Formik
 * inside EditableRow has mounted and its FormikValueTracker has populated
 * the ref).  Used to apply form-adjuster responses that arrive before the
 * row's Formik tree has mounted — without this, the optional-chain call to
 * `setFieldValueRef.current?.(...)` silently no-ops and the update is lost.
 *
 * Polls via requestAnimationFrame with a bounded retry budget so that if
 * Formik never mounts (e.g. edit cancelled mid-flight) the callback simply
 * gives up rather than spinning forever.
 ***************************************************************************/
function applyWhenFormikReady(
   setFieldValueRef: React.MutableRefObject<((name: string, value: any, shouldValidate?: boolean) => void) | null>,
   fn: (setFieldValue: (name: string, value: any, shouldValidate?: boolean) => void) => void,
   isStillCurrent?: () => boolean,
): void
{
   const MAX_ATTEMPTS = 200;  // ~3.2s @ 16ms/frame
   let attempts = 0;
   const tryApply = () =>
   {
      ////////////////////////////////////////////////////////////////////////////////
      // Bail if the edit session this poll was started for is no longer current —  //
      // e.g. user cancelled the row, or moved on to a different row.  Without this //
      // a stale poll could land on a freshly-mounted Formik (the next edit's tree) //
      // and write the previous session's values into it.                           //
      ////////////////////////////////////////////////////////////////////////////////
      if (isStillCurrent && !isStillCurrent())
      {
         return;
      }
      const setFieldValue = setFieldValueRef.current;
      if (setFieldValue)
      {
         fn(setFieldValue);
         return;
      }
      if (++attempts >= MAX_ATTEMPTS)
      {
         console.warn("applyWhenFormikReady: setFieldValueRef never became ready; dropping update");
         return;
      }
      requestAnimationFrame(tryApply);
   };
   tryApply();
}


export interface UseRowEditorArgs
{
   tableMetaData: QTableMetaData;
   editingRow: QRecord | null;
   editingRowId: string | number | null;
   /** Called with the saved-and-refetched record after a successful save. */
   onSaveComplete: (savedRecord: QRecord) => void;
   /** Called after cancel or forced-cancel. */
   onCancel: () => void;
   /** Stable identifier for this row-edit host (one per RecordGridWidget instance,
    *  one per RecordQuery instance).  Used to enforce the "only one row edited at
    *  a time" rule across multiple hosts on the same page — see
    *  globalRowEditRegistry. */
   hostId?: string;
   /** Invoked when an on-load (table-level or field-level) adjuster reports
    *  isFormDisabled=true.  Hosts use this to surface the message as a page-level
    *  alert (e.g. RecordScreenBody's warningMessage / a local alert slot in
    *  RecordQuery) while useRowEditor handles the edit-mode exit via onCancel. */
   onFormDisabled?: (message: string) => void;
   /** Lazy providers used at post-save refetch time so the row-editor uses
    *  the host's active join list / variant.  Required on query-screen
    *  hosts so exposed join-column values flow back into the refreshed
    *  QRecord — without these, makeRows([saved]) produces blanks for
    *  join-backed cells until the next full query runs.  Lazy because the
    *  host-side helpers that compute joins are typically declared further
    *  down the host's render body than the useRowEditor call site. */
   getRefetchQueryJoins?: () => QueryJoin[] | null;
   getRefetchTableVariant?: () => QTableVariant | null;
}

export interface UseRowEditorResult
{
   formFieldDefs: { [fieldName: string]: DynamicFormFieldDefinition };
   initialValues: { [fieldName: string]: any };
   validationSchema: Yup.ObjectSchema<any>;
   isSaving: boolean;
   rowBannerState: RowBannerState;
   setRowBannerState: React.Dispatch<React.SetStateAction<RowBannerState>>;
   savedRowId: string | number | null;
   handleSubmit: (values: any, helpers: any) => Promise<void>;
   handleFieldBlur: (fieldName: string, value: any) => void;
   onFieldChange: (fieldName: string, oldValue: any, newValue: any, valueChangesToMake: { [fieldName: string]: any }) => void;
   formValuesRef: React.MutableRefObject<{ [fieldName: string]: any }>;
   formValuesJSONRef: React.MutableRefObject<string>;
   setFieldValueRef: React.MutableRefObject<((name: string, value: any, shouldValidate?: boolean) => void) | null>;
}

export function useRowEditor({tableMetaData, editingRow, editingRowId, onSaveComplete, onCancel, hostId, onFormDisabled, getRefetchQueryJoins, getRefetchTableVariant}: UseRowEditorArgs): UseRowEditorResult
{
   //////////////////////////////////////////////////////////////////////////////////
   // Global row-edit registry: when this host enters edit mode, claim the active  //
   // slot so other hosts on the page can show their disabled-pencil overlay.  On  //
   // unmount or editingRowId clear, release the claim.  Gated on hostId to stay   //
   // a no-op when callers haven't opted in. */                                    //
   //////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (!hostId || editingRowId == null)
      {
         return;
      }
      claimActiveEditor(hostId);
      return () =>
      {
         releaseActiveEditor(hostId);
      };
   }, [hostId, editingRowId]);

   const [formFieldDefs, setFormFieldDefs] = useState<{ [fieldName: string]: DynamicFormFieldDefinition }>({});
   const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema<any>>(Yup.object().shape({}));
   const [initialValues, setInitialValues] = useState<{ [fieldName: string]: any }>({});
   const [isSaving, setIsSaving] = useState(false);
   const [rowBannerState, setRowBannerState] = useState<RowBannerState>({kind: "none"});
   //////////////////////////////////////////////////////////////////////////////////
   // savedRowId tracks a row that just saved but is no longer in edit — used to    //
   // show the "Saved" label in EditRowActions for a brief window while allowing    //
   // OTHER rows to become editable immediately.  Cleared either by the scheduled   //
   // timer or by a new edit starting.                                              //
   //////////////////////////////////////////////////////////////////////////////////
   const [savedRowId, setSavedRowId] = useState<string | number | null>(null);
   const savedRowIdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const clearSavedRowIdTimer = useCallback(() =>
   {
      if (savedRowIdTimerRef.current)
      {
         clearTimeout(savedRowIdTimerRef.current);
         savedRowIdTimerRef.current = null;
      }
   }, []);

   const formValuesRef = useRef<{ [fieldName: string]: any }>({});
   const formValuesJSONRef = useRef<string>("");
   const setFieldValueRef = useRef<((name: string, value: any, shouldValidate?: boolean) => void) | null>(null);

   ////////////////////////////////////////////////////////////////////////////////
   // Edit-session token: bumped at the start of each build-form-fields effect   //
   // run.  Async adjuster callbacks capture the value at fire time and check it //
   // against editSessionRef.current before applying their response, so a reply  //
   // landing after the user cancelled / switched rows is dropped instead of     //
   // mutating the new session's Formik state.                                   //
   ////////////////////////////////////////////////////////////////////////////////
   const editSessionRef = useRef(0);

   ////////////////////////////////////////////////////////////////////////////////
   // Per-session clone of tableMetaData so adjuster mutations to                //
   // `table.fields` (via runOnLoad/runOnChange's `.set(...)` calls applying     //
   // backend updatedFieldMetaData) stay scoped to this edit session.  The host's //
   // shared metadata singleton stays untouched, preventing leaks into the next   //
   // row's session.                                                              //
   ////////////////////////////////////////////////////////////////////////////////
   const sessionTableRef = useRef<QTableMetaData | null>(null);

   ////////////////////////////////////////////////////////////////////////////////
   // Host callbacks (onCancel, onFormDisabled) are passed as inline arrows from //
   // RecordQuery / RecordGridWidget, so the references change every render.     //
   // We hold them in refs and read .current at call-time, which keeps async     //
   // adjuster paths from invoking a stale closure captured at effect-start.     //
   ////////////////////////////////////////////////////////////////////////////////
   const onCancelRef = useRef(onCancel);
   const onFormDisabledRef = useRef(onFormDisabled);
   useEffect(() =>
   {
      onCancelRef.current = onCancel;
      onFormDisabledRef.current = onFormDisabled;
   });

   // Field rules from table supplemental metadata
   const fieldRules: FieldRule[] = useMemo(() =>
   {
      const mdbMetaData = tableMetaData?.supplementalTableMetaData?.get("materialDashboard");
      return (mdbMetaData?.fieldRules ?? []) as FieldRule[];
   }, [tableMetaData]);

   // Build form fields + initial values when the editing row changes
   useEffect(() =>
   {
      //////////////////////////////////////////////////////////////////////////////////
      // Bump the session token unconditionally so any in-flight adjuster from a      //
      // previous edit (or for a different row) sees a stale token and bails before   //
      // touching state.  Even the no-edit branch needs the bump — cancel transitions //
      // through here.                                                                //
      //////////////////////////////////////////////////////////////////////////////////
      editSessionRef.current++;
      const mySession = editSessionRef.current;
      const isStillCurrent = () => editSessionRef.current === mySession;

      if (!editingRow || !tableMetaData)
      {
         sessionTableRef.current = null;
         setFormFieldDefs({});
         setInitialValues({});
         setValidationSchema(Yup.object().shape({}));
         setRowBannerState({kind: "none"});
         return;
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // Clone the host's tableMetaData so adjuster mutations (table.fields.set in     //
      // formAdjustersAndRules) stay scoped to this session.  All in-effect            //
      // references below use the clone; runOnChange's useCallback also reads from     //
      // sessionTableRef.  QTableMetaData.clone() recursively clones fields, sections, //
      // capabilities, etc., so backend-supplied updatedFieldMetaData lands on the     //
      // clone and the host's shared singleton stays untouched between row sessions.   //
      ///////////////////////////////////////////////////////////////////////////////////
      const sessionTable = tableMetaData.clone();
      sessionTableRef.current = sessionTable;

      ////////////////////////////////////////////////////////////////////////////
      // Entering a new edit — dismiss any lingering "Saved" indicator from the //
      // previous row so the new row's overlay doesn't collide with it.         //
      ////////////////////////////////////////////////////////////////////////////
      clearSavedRowIdTimer();
      setSavedRowId(null);

      //////////////////////////////////////////////////////////////////////////////////////
      // Seed PV initialDisplayValue from the editing row's displayValues so dropdowns    //
      // show e.g. "Wednesday" rather than blank when entering edit mode.  The underlying //
      // form value is still the raw ID (e.g. 3), driven by initialValues below.          //
      //////////////////////////////////////////////////////////////////////////////////////
      const fieldsArray = Array.from(sessionTable.fields.values());
      const initialDisplayValues = editingRow.displayValues ?? new Map<string, string>();
      const {dynamicFormFields, formValidations} = DynamicFormUtils.buildDynamicFormFields(sessionTable, initialDisplayValues);
      setFormFieldDefs(dynamicFormFields);
      setValidationSchema(Yup.object().shape(formValidations));

      ////////////////////////////////////////////////////////////////////////////////
      // Build initial values from record.  Match RecordScreen's behavior           //
      // (useRecordScreen.ts): assign QRecord.values.get(name) directly — empty /   //
      // missing fields stay `undefined` so that JSON.stringify omits them from the //
      // `allValues` payload sent to form-adjusters.  Defaulting to "" caused the   //
      // adjuster to receive empty strings for every missing field, which differs   //
      // from RecordScreen and can confuse backend adjuster logic.                  //
      ////////////////////////////////////////////////////////////////////////////////
      const init: { [fieldName: string]: any } = {};
      sessionTable.fields.forEach((f, name) =>
      {
         init[name] = editingRow.values?.get(name);
      });
      DynamicFormUtils.formatDateTimeValuesForForm(init, sessionTable);
      setInitialValues(init);
      formValuesRef.current = {...init};
      formValuesJSONRef.current = JSON.stringify(init);
      setRowBannerState({kind: "none"});

      ////////////////////////////////////////////////////
      // Run TABLE-LEVEL on-load adjuster if configured //
      ////////////////////////////////////////////////////
      const mdbMetaData = sessionTable.supplementalTableMetaData?.get("materialDashboard");
      if (mdbMetaData?.onLoadFormAdjuster)
      {
         const callbacks: FormAdjusterCallbacks = {
            applyUpdatedSections: () =>
            {
               ///////////////////
               // no-op in grid //
               ///////////////////
            },
            ////////////////////////////////////////////////////////////////////////////
            // formDisabled bails out of edit mode entirely and delegates the message //
            // to the page-level alert via onFormDisabled.  The inline row-level      //
            // banner path was removed in favor of a consistent page-level UX — see   //
            // RecordScreenContext.onChildFormDisabled / RecordQuery's local alert.   //
            //                                                                        //
            // shouldApplyFormDisabled returns true unconditionally — the page alert  //
            // and cancel must fire even if the editingRow useEffect re-runs between  //
            // the POST going out and resolving (e.g. an unrelated state update bumps //
            // editSessionRef before the response lands).  Gating only the SIDE       //
            // EFFECTS that touch the current session's state on isStillCurrent keeps //
            // a stale response from corrupting a fresh edit, while still letting the //
            // user see the disabled-message they triggered.                          //
            ////////////////////////////////////////////////////////////////////////////
            onFormDisabled: (message) =>
            {
               onFormDisabledRef.current?.(message);
               if (!isStillCurrent())
               {
                  return;
               }
               onCancelRef.current();
            },
            shouldApplyFormDisabled: () => true,
            disableAllFields: () =>
            {
               if (!isStillCurrent())
               {
                  return;
               }
               setFormFieldDefs((prev) =>
               {
                  const next = {...prev};
                  for (const k in next)
                  {
                     next[k] = {...next[k], isEditable: false};
                  }
                  return next;
               });
            },
         };
         ///////////////////////////////////////////////////////////////////////////////
         // Build a "compact" values object for the adjuster payload: drop undefined, //
         // null, and empty-string fields so the backend sees missing keys instead of //
         // explicit blanks.  (JSON.stringify inside runOnLoadFormAdjuster would      //
         // still serialize "" as a present-but-empty value, which can confuse some   //
         // adjusters expecting `!record.hasField(name)`.)                            //
         ///////////////////////////////////////////////////////////////////////////////
         const compactValuesForAdjuster: { [k: string]: any } = {};
         for (const k in init)
         {
            const v = init[k];
            if (v !== undefined && v !== null && v !== "")
            {
               compactValuesForAdjuster[k] = v;
            }
         }
         ///////////////////////////////////////////////////////////////////////////////
         // Snapshot pre-adjuster values so after runOnLoadFormAdjuster mutates its   //
         // `values` param with updatedFieldValues / fieldsToClear, we can diff and   //
         // propagate the deltas into Formik via setFieldValue.  Without this, the    //
         // adjuster's updatedFieldValues are lost entirely — runOnLoadFormAdjuster   //
         // only mutates its local `values` object, which is a *copy* and never flows //
         // back into the Formik-bound initialValues.                                 //
         ///////////////////////////////////////////////////////////////////////////////
         const snapshotBeforeAdjuster = {...compactValuesForAdjuster};
         const displayValuesMap = new Map<string, string>();
         runOnLoadFormAdjuster(sessionTable, compactValuesForAdjuster, displayValuesMap, callbacks, isStillCurrent)
            .then(() =>
            {
               if (!isStillCurrent())
               {
                  return;
               }
               //////////////////////////////////////////////////////////////////////////////////
               // runOnLoadFormAdjuster replaces entries in sessionTable.fields (the per-      //
               // session clone) with new QFieldMetaData instances when the response returns   //
               // updatedFieldMetaData (e.g. a field flipping to isEditable:false).  Re-derive //
               // formFieldDefs from the mutated fields map so the grid's cell renderer sees   //
               // the refreshed metadata — without this, fields stay editable even though the  //
               // session's field metadata says otherwise.  The host's shared tableMetaData    //
               // remains untouched.                                                           //
               //////////////////////////////////////////////////////////////////////////////////
               const refreshedFieldsArray = Array.from(sessionTable.fields.values());
               const {dynamicFormFields: refreshedFormFields} = DynamicFormUtils.getFormData(refreshedFieldsArray);
               DynamicFormUtils.addPossibleValueProps(refreshedFormFields, refreshedFieldsArray, sessionTable.name, null, initialDisplayValues);
               setFormFieldDefs((prev) =>
               {
                  ///////////////////////////////////////////////////////////////////////////////
                  // Preserve isEditable:false that was set by the disableAllFields callback   //
                  // (fired synchronously by runOnLoadFormAdjuster before this .then runs).    //
                  // Without this guard, re-deriving from tableMetaData.fields would re-enable //
                  // fields that the adjuster explicitly disabled via isFormDisabled.          //
                  ///////////////////////////////////////////////////////////////////////////////
                  const merged = {...refreshedFormFields};
                  for (const k in prev)
                  {
                     if (prev[k] && !prev[k].isEditable && merged[k])
                     {
                        merged[k] = {...merged[k], isEditable: false};
                     }
                  }
                  return merged;
               });

               applyWhenFormikReady(setFieldValueRef, (setFieldValue) =>
               {
                  for (const k in compactValuesForAdjuster)
                  {
                     if (compactValuesForAdjuster[k] !== snapshotBeforeAdjuster[k])
                     {
                        setFieldValue(k, compactValuesForAdjuster[k]);
                     }
                  }
               }, isStillCurrent);
            })
            .catch((e) =>
            {
               console.error("on-load adjuster failed", e);
            });
      }

      ////////////////////////////////////////////////////////////////////////////////
      // FIELD-LEVEL on-load adjusters — mirrors DynamicForm's behavior.  For each  //
      // field whose supplemental metadata has `onLoadFormAdjuster: true`, POST to  //
      // the form-adjuster endpoint with event=onLoad, then apply the response back //
      // through Formik (via setFieldValueRef) so the user sees the adjusted value. //
      ////////////////////////////////////////////////////////////////////////////////
      dynamicFormFields && fieldsArray.forEach((fieldMetaData) =>
      {
         const mdbFieldMetaData = (fieldMetaData as any).supplementalFieldMetaData?.get?.("materialDashboard");
         if (!mdbFieldMetaData?.onLoadFormAdjuster)
         {
            return;
         }
         const identifier = mdbFieldMetaData.formAdjusterIdentifier;
         if (!identifier)
         {
            return;
         }
         const postBody = new FormData();
         postBody.append("event", "onLoad");
         postBody.append("fieldName", fieldMetaData.name);
         postBody.append("newValue", init[fieldMetaData.name] ?? "");
         postBody.append("allValues", JSON.stringify(init));
         qController.axiosRequest(
            {
               method:  "post",
               url:     `/material-dashboard-backend/form-adjuster/${encodeURIComponent(identifier)}/onLoad`,
               data:    postBody,
               headers: qController.defaultMultipartFormDataHeaders(),
            })
            .then((response: any) =>
            {
               if (!isStillCurrent())
               {
                  return;
               }
               ////////////////////////////////////////////////////////////////////////////
               // Apply updated values via setFieldValueRef so Formik reflects them.     //
               // setFieldValueRef is populated by EditableRow's FormikValueTracker once //
               // Formik mounts — may be null on the first pass (race with the async     //
               // POST), so gate the application on the ref becoming ready.              //
               ////////////////////////////////////////////////////////////////////////////
               const updatedFieldValues = response?.updatedFieldValues ?? {};
               const fieldsToClear: string[] = response?.fieldsToClear ?? [];
               if (Object.keys(updatedFieldValues).length > 0 || fieldsToClear.length > 0)
               {
                  applyWhenFormikReady(setFieldValueRef, (setFieldValue) =>
                  {
                     for (const fn in updatedFieldValues)
                     {
                        setFieldValue(fn, updatedFieldValues[fn]);
                     }
                     for (const fn of fieldsToClear)
                     {
                        setFieldValue(fn, "");
                     }
                  }, isStillCurrent);
               }
               if (response?.isFormDisabled)
               {
                  const message = response.formDisabledMessage ?? "You are not allowed to edit this record.";
                  onFormDisabledRef.current?.(message);
                  onCancelRef.current();
               }
            })
            .catch((e: any) => console.error(`field on-load adjuster failed for ${fieldMetaData.name}`, e));
      });
   }, [tableMetaData, editingRow, clearSavedRowIdTimer]);

   ////////////////////////////////////////////////////////////////////////////////
   // Cancel any outstanding savedRowId timer on unmount.                         //
   ////////////////////////////////////////////////////////////////////////////////
   useEffect(() => () => clearSavedRowIdTimer(), [clearSavedRowIdTimer]);

   ////////////////////////////////////////////////////////////////////////////////
   // On-change adjusters can themselves mutate form values via the response's   //
   // updatedFieldValues, which flows through setFieldValue → Formik re-render → //
   // FormikValueTracker → onFieldChange.  Without a guard, any PV/checkbox/file //
   // field in that response path re-enters the adjuster branch and fires ANOTHER //
   // POST, producing an infinite /onChange loop in the browser.                 //
   //                                                                            //
   // The guard below blocks re-entrant adjuster firing while one is already in  //
   // flight, clearing the flag two animation frames after the promise resolves  //
   // so that React has a chance to flush the setFieldValue cascade + resulting  //
   // FormikValueTracker re-renders before we allow new adjuster calls.          //
   ////////////////////////////////////////////////////////////////////////////////
   const adjusterInFlightRef = useRef<boolean>(false);

   const runOnChangeAdjusterWithGridCallbacks = useCallback((fieldName: string, newValue: any, mdbFieldMetaData: any): Promise<void> =>
   {
      if (adjusterInFlightRef.current)
      {
         return Promise.resolve();
      }
      ////////////////////////////////////////////////////////////////////////////////
      // Capture the session token at fire time.  If the user cancels or moves to   //
      // a different row before the response lands, the response is dropped instead //
      // of mutating the new session's state.                                       //
      ////////////////////////////////////////////////////////////////////////////////
      const mySession = editSessionRef.current;
      const isStillCurrent = () => editSessionRef.current === mySession;
      const callbacks: FormAdjusterCallbacks = {
         applyUpdatedSections: () =>
         {
            ///////////
            // no-op //
            ///////////
         },
         onFormDisabled: (message) =>
         {
            /////////////////////////////////////////////////////////////////////////
            // Same page-level-alert routing as the on-load path.  If an on-change //
            // adjuster flips the form into a disabled state the cleanest thing is //
            // to bail out of the current edit and report via the page alert — the //
            // in-progress input values wouldn't make sense to keep and the user   //
            // has already been told.                                              //
            /////////////////////////////////////////////////////////////////////////
            if (!isStillCurrent())
            {
               return;
            }
            onFormDisabledRef.current?.(message);
            onCancelRef.current();
         },
         shouldApplyFormDisabled: () => isStillCurrent(),
         disableAllFields: () =>
         {
            if (!isStillCurrent())
            {
               return;
            }
            setFormFieldDefs((prev) =>
            {
               const next = {...prev};
               for (const k in next) next[k] = {...next[k], isEditable: false};
               return next;
            });
         },
      };
      adjusterInFlightRef.current = true;
      /////////////////////////////////////////////////////////////////////////////////
      // Pass the session-cloned table (sessionTableRef.current) so backend          //
      // updatedFieldMetaData mutations land on the per-session clone rather than    //
      // the host's shared singleton.  Falls back to the original prop if the build  //
      // effect hasn't populated the ref yet (shouldn't happen — adjuster fires only //
      // when an edit is active — but defensive).                                    //
      /////////////////////////////////////////////////////////////////////////////////
      const tableForAdjuster = sessionTableRef.current ?? tableMetaData;
      return runOnChangeFormAdjuster({
         tableName: tableForAdjuster.name,
         table: tableForAdjuster,
         fieldName,
         newValue,
         materialDashboardFieldMetaData: mdbFieldMetaData,
         formFields: formFieldDefs,
         formValuesRef,
         setFieldValueRef,
         onFormFieldsChanged: () => setFormFieldDefs((prev) => ({...prev})),
         callbacks,
         isStillCurrent,
         ///////////////////////////////////////////////////////////////////////////////
         // The Yup schema is built once when the edit session starts and doesn't     //
         // track later field mutations — recompose it from the session table (which  //
         // runOnChangeFormAdjuster has already updated) so adjuster-driven           //
         // isRequired changes reach validation, same as the record-screen host.      //
         ///////////////////////////////////////////////////////////////////////////////
         onFieldMetaDataReplaced: () =>
         {
            if (!isStillCurrent() || !sessionTableRef.current)
            {
               return;
            }
            const formValidations: { [key: string]: Yup.Schema } = {};
            sessionTableRef.current.fields.forEach((field, name) =>
            {
               formValidations[name] = DynamicFormUtils.getValidationForField(field);
            });
            setValidationSchema(Yup.object().shape(formValidations));
         },
      }).finally(() =>
      {
         requestAnimationFrame(() =>
         {
            requestAnimationFrame(() =>
            {
               adjusterInFlightRef.current = false;
            });
         });
      });
   }, [tableMetaData, formFieldDefs]);

   /////////////////////////////////////////////////////////////////////////////////
   // Field-change handler: evaluate field rules only.  On-change adjuster firing //
   // is driven directly from cell components (PV, boolean, blob via onChange;    //
   // text/number via onBlur), which fires in lock-step with the user action      //
   // rather than a render-tick later.                                            //
   /////////////////////////////////////////////////////////////////////////////////
   const onFieldChange = useCallback((fieldName: string, oldValue: any, newValue: any, valueChangesToMake: { [fieldName: string]: any }) =>
   {
      evaluateFieldRules(fieldRules, fieldName, newValue, valueChangesToMake, () =>
      {
         ///////////////////////////////////////////////////////////////////
         // RELOAD_WIDGET is a no-op in grid context (documented in spec) //
         ///////////////////////////////////////////////////////////////////
      });
   }, [fieldRules]);

   ///////////////////////////////////////////////////////////////////////////////////
   // Direct trigger for the on-change form adjuster — used by cell components      //
   // that want to fire the adjuster immediately on value commit (PV selection,     //
   // boolean toggle, blob pick) or on blur (text / number inputs).                 //
   //                                                                               //
   // Matches RecordScreen/DynamicForm behavior where onChange for PV/file/checkbox //
   // triggers the adjuster synchronously with the user action, rather than         //
   // waiting for FormikValueTracker's JSON-compare render to detect the change.    //
   ///////////////////////////////////////////////////////////////////////////////////
   const triggerFieldAdjuster = useCallback((fieldName: string, value: any) =>
   {
      if (adjusterInFlightRef.current)
      {
         return;
      }
      const field = formFieldDefs[fieldName];
      if (!field)
      {
         return;
      }
      const mdbFieldMetaData = field.fieldMetaData?.supplementalFieldMetaData?.get("materialDashboard");
      if (mdbFieldMetaData?.onChangeFormAdjuster)
      {
         void runOnChangeAdjusterWithGridCallbacks(fieldName, value, mdbFieldMetaData);
      }
   }, [formFieldDefs, runOnChangeAdjusterWithGridCallbacks]);

   //////////////////////////////////////////////////////////////////////////////////
   // Alias kept for backwards compatibility with RowEditContextValue's             //
   // handleFieldBlur and external callers.  Same behavior as triggerFieldAdjuster. //
   //////////////////////////////////////////////////////////////////////////////////
   const handleFieldBlur = triggerFieldAdjuster;

   const handleSubmit = useCallback(async (values: any, _helpers: any) =>
   {
      if (!editingRow || !tableMetaData)
      {
         return;
      }
      setIsSaving(true);
      setRowBannerState({kind: "none"});
      try
      {
         //////////////////////////////////////////////////////////////////////////////
         // Use the per-session table clone for save sanitization too — adjuster     //
         // responses may have replaced field type metadata, and the clone reflects  //
         // the latest session-scoped view.  Name + primaryKeyField are identical to //
         // the original prop, so qController.update is unaffected by the choice.    //
         //////////////////////////////////////////////////////////////////////////////
         const tableForSave = sessionTableRef.current ?? tableMetaData;
         const valuesToPost = DynamicFormUtils.sanitizeFormValuesForSave(values, tableForSave, initialValues);

         const recordId = editingRow.values.get(tableForSave.primaryKeyField) as string;
         await qController.update(tableForSave.name, recordId, valuesToPost);

         ////////////////////////////////////////////////////////////////////////////////////
         // Re-fetch with the host's active table variant + query joins so exposed         //
         // join-column values come back populated.  Without these, makeRows on the        //
         // saved record paints blanks in join-backed cells until the next full            //
         // query runs.  qController.get signature:                                        //
         //   get(tableName, primaryKey, tableVariant?, includeAssociations?, queryJoins?) //
         //                                                                                //
         // Caveat (not handled): the refetch returns ONE record by primary key.           //
         // If the edit changed a field that participates in an exposed 1:N join,          //
         // the join match set can shift in ways a single-row refresh cannot fully         //
         // reflect.  Concrete cases:                                                      //
         //   - 1 match -> N matches: only one of the new matches lands in the grid;       //
         //     the other N-1 stay missing until the next full query.                      //
         //   - Original query returned N duplicated rows for the same left PK (1:N        //
         //     join flattened into N rows): we update only the row whose                  //
         //     __rowIndex matches editingRowId; the sibling N-1 rows show stale           //
         //     left-side values until the next full query.                                //
         // A proper fix would trigger a full host requery when                            //
         // tableMetaData.exposedJoins?.length > 0; deferred for now.                      //
         ////////////////////////////////////////////////////////////////////////////////////
         const refreshed = await qController.get(
            tableForSave.name,
            recordId,
            getRefetchTableVariant?.() ?? null,
            null,
            getRefetchQueryJoins?.() ?? null,
         );

         ////////////////////////////////////////////////////////////////////////////
         // Exit edit mode and refresh the row's display data IMMEDIATELY so other //
         // rows become editable right away.  Keep a "savedRowId" marker for       //
         // SAVE_SUCCESS_BANNER_MS so EditRowActions can render a "Saved" label    //
         // in place of the pencil on that specific row for the duration.          //
         ////////////////////////////////////////////////////////////////////////////
         const justSavedRowId = editingRowId;
         setRowBannerState({kind: "none"});
         onSaveComplete(refreshed);
         clearSavedRowIdTimer();
         setSavedRowId(justSavedRowId);
         savedRowIdTimerRef.current = setTimeout(() =>
         {
            setSavedRowId((curr) => (curr === justSavedRowId ? null : curr));
            savedRowIdTimerRef.current = null;
         }, SAVE_SUCCESS_BANNER_MS);
      }
      catch (e: any)
      {
         const message = e?.message ?? (typeof e === "string" ? e : "Save failed.");
         setRowBannerState({kind: "saveError", message});
      }
      finally
      {
         setIsSaving(false);
      }
   }, [editingRow, editingRowId, tableMetaData, initialValues, onSaveComplete, clearSavedRowIdTimer]);

   return {
      formFieldDefs,
      initialValues,
      validationSchema,
      isSaving,
      rowBannerState,
      setRowBannerState,
      savedRowId,
      handleSubmit,
      handleFieldBlur,
      onFieldChange,
      formValuesRef,
      formValuesJSONRef,
      setFieldValueRef,
   };
}
